import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useAbility } from "@casl/vue";
import type { RouteLocationRaw } from "vue-router";

import {
  ECotreatmentTabs,
  EPatientDashboardTab,
  EPatientTabs,
  EReferralDashboardTab,
} from "@/types/filter/tableFilters";

const DASHBOARD_PATHS = {
  allPatients: "/dashboard/all-patients",
  cotreatment: "/dashboard/co-treatment",
  referrals: "/dashboard/referrals",
} as const;

type TDashboardDestination = {
  path: (typeof DASHBOARD_PATHS)[keyof typeof DASHBOARD_PATHS];
  tab: EPatientDashboardTab | ECotreatmentTabs | EReferralDashboardTab;
  /** Page-qualified label — the three dashboards' tabs share one dropdown. */
  labelKey: string;
  /** Gate matching the section the destination lives in. */
  requiresReferrals?: boolean;
};

/**
 * Where each legacy dashboard tab lives in the split dashboard. The stored
 * preference is still an `EPatientTabs` value — the old single table was
 * broken up into three pages, and every one of its tabs survived as a
 * `?tab=` on one of them, so the setting needs remapping rather than migrating.
 */
export const DASHBOARD_DESTINATIONS: Record<EPatientTabs, TDashboardDestination> = {
  [EPatientTabs.ALL]: {
    path: DASHBOARD_PATHS.allPatients,
    tab: EPatientDashboardTab.ALL,
    labelKey: "models.patient.table.predefinedFilters.all",
  },
  [EPatientTabs.ACTION_REQUIRED]: {
    path: DASHBOARD_PATHS.cotreatment,
    tab: ECotreatmentTabs.ACTIONS_NEEDED,
    labelKey: "models.patient.table.predefinedFilters.qualified.cotreatmentActionsNeeded",
  },
  [EPatientTabs.IN_TREATMENT_PLAN]: {
    path: DASHBOARD_PATHS.cotreatment,
    tab: ECotreatmentTabs.IN_PROGRESS,
    labelKey: "models.patient.table.predefinedFilters.qualified.cotreatmentInProgress",
  },
  [EPatientTabs.IN_TREATMENT]: {
    path: DASHBOARD_PATHS.cotreatment,
    tab: ECotreatmentTabs.IN_TREATMENT,
    labelKey: "models.patient.table.predefinedFilters.qualified.cotreatmentInTreatment",
  },
  [EPatientTabs.REFERRALS_ACTION_REQUIRED]: {
    path: DASHBOARD_PATHS.referrals,
    tab: EReferralDashboardTab.ACTIONS_NEEDED,
    labelKey: "models.patient.table.predefinedFilters.qualified.referralsActionsNeeded",
    requiresReferrals: true,
  },
  [EPatientTabs.REFERRALS_IN]: {
    path: DASHBOARD_PATHS.referrals,
    tab: EReferralDashboardTab.REFERRALS_IN,
    labelKey: "models.patient.table.predefinedFilters.qualified.referralsIn",
    requiresReferrals: true,
  },
  [EPatientTabs.REFERRALS_OUT]: {
    path: DASHBOARD_PATHS.referrals,
    tab: EReferralDashboardTab.REFERRALS_OUT,
    labelKey: "models.patient.table.predefinedFilters.qualified.referralsOut",
    requiresReferrals: true,
  },
  [EPatientTabs.ARCHIVED]: {
    path: DASHBOARD_PATHS.allPatients,
    tab: EPatientDashboardTab.ARCHIVED,
    labelKey: "models.patient.table.predefinedFilters.archived",
  },
};

export const DEFAULT_DASHBOARD_TAB = EPatientTabs.ACTION_REQUIRED;

/**
 * Display order for the setting, grouped by page. Explicit because the numeric
 * enum keys iterate in value order, which scatters the referral entries.
 * `ARCHIVED` is omitted — it duplicates "all" until it gets a real destination.
 */
const DASHBOARD_TAB_ORDER: EPatientTabs[] = [
  EPatientTabs.ALL,
  EPatientTabs.ACTION_REQUIRED,
  EPatientTabs.IN_TREATMENT_PLAN,
  EPatientTabs.IN_TREATMENT,
  EPatientTabs.REFERRALS_ACTION_REQUIRED,
  EPatientTabs.REFERRALS_IN,
  EPatientTabs.REFERRALS_OUT,
];

const toDestination = (value: unknown): TDashboardDestination => {
  const tab = Number(value);

  return (
    DASHBOARD_DESTINATIONS[tab as EPatientTabs] ?? DASHBOARD_DESTINATIONS[DEFAULT_DASHBOARD_TAB]
  );
};

/**
 * Resolves the stored preference to a route the split dashboard actually serves.
 * A referral destination held by someone who can no longer read referrals falls
 * back to the default rather than landing them on a section they lost access to.
 */
export const dashboardDefaultRoute = (
  value: unknown,
  canReadReferrals = true
): RouteLocationRaw => {
  const destination = toDestination(value);
  const { path, tab } =
    destination.requiresReferrals && !canReadReferrals
      ? DASHBOARD_DESTINATIONS[DEFAULT_DASHBOARD_TAB]
      : destination;

  return { path, query: { tab } };
};

/** Options for the "Default Dashboard Tab" setting, gated the same way the tabs are. */
export const useDashboardTabOptions = () => {
  const { t } = useI18n();
  const { can } = useAbility();

  return computed(() => {
    const canReadReferrals = can($permActions.READ, $permSubjects.REFERRAL__REFERRAL);

    return DASHBOARD_TAB_ORDER.filter(
      (tab) => !DASHBOARD_DESTINATIONS[tab].requiresReferrals || canReadReferrals
    ).map((tab) => ({ value: String(tab), title: t(DASHBOARD_DESTINATIONS[tab].labelKey) }));
  });
};
